/** The portal tour (DESIGN §20): page by page, one part lit at a time; it only shows. */
import "./tour.css";
import { type ReactNode, createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { store } from "../portal/browser.js";
import { liftsKey, loadLifts, useResource } from "../portal/data.js";
import { type Chapter, type ChapterID, type Step, type TourContext, chapters } from "./steps.js";

export const TOUR_NOTICE_KEY = "tour-notice";

interface TourValue {
  chapters: Chapter[];
  start: (chapter: ChapterID | "all") => void;
  /** Opens the list of chapters. */
  pick: () => void;
  running: boolean;
}

const Ctx = createContext<TourValue>({ chapters: [], start: () => {}, pick: () => {}, running: false });

export function useTour(): TourValue {
  return useContext(Ctx);
}

interface Run {
  steps: (Step & { chapter: string })[];
  index: number;
}

type Rect = { x: number; y: number; w: number; h: number };

const WAIT_MS = 2000;
const PAD = 8;

function visible(selectors: string[]): HTMLElement | null {
  for (const s of selectors) {
    for (const el of Array.from(document.querySelectorAll<HTMLElement>(s))) {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) return el;
    }
  }
  return null;
}

const reduceMotion = () => typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export function TourProvider({ context, children }: { context: TourContext; children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [run, setRun] = useState<Run | null>(null);
  const [picking, setPicking] = useState(false);
  const lifts = useResource(run ? liftsKey : null, loadLifts);
  const ctxRef = useRef(context);
  ctxRef.current = { ...context, firstLift: lifts.value?.list[0]?.key ?? null };
  const all = useMemo(() => chapters(context), [context]);

  const start = useCallback(
    (id: ChapterID | "all") => {
      const picked = id === "all" ? all : all.filter((c) => c.id === id);
      const steps = picked.flatMap((c) => c.steps.map((s) => ({ ...s, chapter: c.title })));
      if (!steps.length) return;
      store(TOUR_NOTICE_KEY, "closed");
      setPicking(false);
      setRun({ steps, index: 0 });
    },
    [all],
  );

  const pick = useCallback(() => setPicking(true), []);
  const value = useMemo(() => ({ chapters: all, start, pick, running: run != null }), [all, start, pick, run]);
  return (
    <Ctx.Provider value={value}>
      {children}
      {picking ? <TourPicker chapters={all} onPick={start} onClose={() => setPicking(false)} /> : null}
      {run ? <TourLayer key="tour" run={run} setRun={setRun} ctxRef={ctxRef} navigate={navigate} pathname={location.pathname} /> : null}
    </Ctx.Provider>
  );
}

function TourLayer({
  run,
  setRun,
  ctxRef,
  navigate,
  pathname,
}: {
  run: Run;
  setRun: (r: Run | null) => void;
  ctxRef: { current: TourContext };
  navigate: (to: string) => void;
  pathname: string;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const card = useRef<HTMLDivElement>(null);
  const next = useRef<HTMLButtonElement>(null);
  const opener = useRef<Element | null>(null);
  const [target, setTarget] = useState<HTMLElement | null>(null);
  const [searching, setSearching] = useState(true);
  const [rect, setRect] = useState<Rect | null>(null);
  const [cardSize, setCardSize] = useState({ w: 340, h: 180 });
  const [viewport, setViewport] = useState({ w: window.innerWidth, h: window.innerHeight });
  const step = run.steps[run.index]!;
  const last = run.index === run.steps.length - 1;

  const end = useCallback(() => setRun(null), [setRun]);
  const go = useCallback((d: number) => setRun({ ...run, index: Math.max(0, Math.min(run.steps.length - 1, run.index + d)) }), [run, setRun]);

  useLayoutEffect(() => {
    const d = dialog.current;
    if (!d) return;
    opener.current = document.activeElement;
    if (typeof d.showModal === "function") d.showModal();
    else d.setAttribute("open", "");
    return () => {
      if (typeof d.close === "function" && d.open) d.close();
      const back = opener.current;
      if (back instanceof HTMLElement && back.isConnected) back.focus();
    };
  }, []);

  useEffect(() => {
    const onPop = () => end();
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [end]);

  // The step's page: a path given as a function is read now, so it sees the team as it is.
  // The lifts list may still be on its way when its step comes; the step is read again when it lands.
  const firstLift = ctxRef.current.firstLift;
  const wanted = useMemo(() => {
    const p = typeof step.path === "function" ? step.path(ctxRef.current) : step.path;
    return p === undefined ? undefined : p;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run.index, run.steps, firstLift]);

  useEffect(() => {
    if (wanted && wanted !== pathname) navigate(wanted);
  }, [wanted, pathname, navigate]);

  useEffect(() => {
    setTarget(null);
    setRect(null);
    const there = wanted === undefined || wanted === null || wanted === pathname;
    if (!there) return;
    if (!step.targets.length || wanted === null) {
      setSearching(false);
      return;
    }
    setSearching(true);
    const began = Date.now();
    let timer = 0;
    const look = () => {
      const el = visible(step.targets);
      if (el) {
        const tall = el.getBoundingClientRect().height > window.innerHeight * 0.6;
        el.scrollIntoView({ block: tall ? "start" : "center", behavior: reduceMotion() ? "auto" : "smooth" });
        setTarget(el);
        setSearching(false);
      } else if (Date.now() - began >= WAIT_MS) setSearching(false);
      else timer = window.setTimeout(look, 100);
    };
    look();
    return () => window.clearTimeout(timer);
  }, [step, wanted, pathname]);

  useEffect(() => {
    if (!target) return;
    let frame = 0;
    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const r = target.getBoundingClientRect();
        setRect({ x: r.left, y: r.top, w: r.width, h: r.height });
        setViewport({ w: window.innerWidth, h: window.innerHeight });
      });
    };
    measure();
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
    };
  }, [target]);

  useLayoutEffect(() => {
    const el = card.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (r.width && (Math.abs(r.width - cardSize.w) > 1 || Math.abs(r.height - cardSize.h) > 1)) setCardSize({ w: r.width, h: r.height });
  });

  useEffect(() => {
    if (!searching) next.current?.focus();
  }, [searching, run.index]);

  const phone = ctxRef.current.phone;
  const hole = rect ? clip({ x: rect.x - PAD, y: rect.y - PAD, w: rect.w + PAD * 2, h: rect.h + PAD * 2 }, viewport) : null;
  const shown = !searching;
  const missing = shown && !target;
  const body = missing && step.instead ? step.instead : step.body;
  const place = phone ? null : placeCard(hole, cardSize, viewport);
  const titleID = "tour-title";
  const bodyID = "tour-body";

  return (
    <dialog
      ref={dialog}
      className="tour"
      aria-labelledby={titleID}
      aria-describedby={bodyID}
      onCancel={(e) => {
        e.preventDefault();
        end();
      }}
      onKeyDown={(e) => {
        if (e.key === "ArrowRight") {
          e.preventDefault();
          if (last) end();
          else go(1);
        } else if (e.key === "ArrowLeft") {
          e.preventDefault();
          go(-1);
        } else if (e.key === "Escape") {
          e.preventDefault();
          end();
        }
      }}
    >
      <svg className="tour-shade" width="100%" height="100%" aria-hidden="true" focusable="false">
        <defs>
          <mask id="tour-hole">
            <rect width="100%" height="100%" fill="white" />
            {hole ? <rect x={hole.x} y={hole.y} width={hole.w} height={hole.h} rx={12} fill="black" /> : null}
          </mask>
        </defs>
        <rect className="tour-dim" width="100%" height="100%" mask="url(#tour-hole)" />
        {hole ? <rect className="tour-ring" x={hole.x} y={hole.y} width={hole.w} height={hole.h} rx={12} /> : null}
      </svg>
      <div
        ref={card}
        className={"tour-card" + (phone ? " is-phone" : "") + (place ? "" : phone ? "" : " is-centred") + (shown ? "" : " is-waiting")}
        style={place ? { left: place.x, top: place.y } : undefined}
      >
        <p className="tour-chapter">{step.chapter}</p>
        <h2 id={titleID} className="tour-title">
          {step.title}
        </h2>
        <div id={bodyID}>
          <p className="tour-body">{shown ? body : "…"}</p>
          <p className="tour-count num">
            {run.index + 1} of {run.steps.length}
          </p>
        </div>
        <div className="tour-actions">
          <button type="button" className="btn tour-close" onClick={end}>
            Close<span className="sr"> the tour</span>
          </button>
          <span className="tour-spacer" />
          {run.index > 0 ? (
            <button type="button" className="btn" onClick={() => go(-1)}>
              Back
            </button>
          ) : null}
          <button ref={next} type="button" className="btn tour-next" onClick={() => (last ? end() : go(1))}>
            {last ? "Done" : "Next"}
          </button>
        </div>
      </div>
    </dialog>
  );
}

function TourPicker({ chapters: list, onPick, onClose }: { chapters: Chapter[]; onPick: (id: ChapterID | "all") => void; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  useLayoutEffect(() => {
    const d = ref.current;
    if (!d) return;
    const back = document.activeElement;
    if (typeof d.showModal === "function") d.showModal();
    else d.setAttribute("open", "");
    return () => {
      if (back instanceof HTMLElement && back.isConnected) back.focus();
    };
  }, []);
  const total = list.reduce((n, c) => n + c.steps.length, 0);
  const row = (id: ChapterID | "all", title: string, steps: number) => (
    <li key={id}>
      <button type="button" className="tour-pick" onClick={() => onPick(id)}>
        <span>{title}</span>
        <span className="tour-pick-n num">{steps} steps</span>
      </button>
    </li>
  );
  return (
    <dialog
      ref={ref}
      className="tour-picker"
      aria-labelledby="tour-picker-title"
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
    >
      <h2 id="tour-picker-title" className="tour-title">
        Take the tour
      </h2>
      <p className="tour-body">It goes page by page through your team as it is, and only shows: nothing is changed.</p>
      <ul className="tour-picks">
        {row("all", "All of it", total)}
        {list.map((c) => row(c.id, c.title, c.steps.length))}
      </ul>
      <div className="tour-actions">
        <span className="tour-spacer" />
        <button type="button" className="btn" onClick={onClose}>
          Cancel
        </button>
      </div>
    </dialog>
  );
}

function clip(r: Rect, v: { w: number; h: number }): Rect | null {
  const x = Math.max(r.x, 4);
  const y = Math.max(r.y, 4);
  const right = Math.min(r.x + r.w, v.w - 4);
  const bottom = Math.min(r.y + r.h, v.h - 4);
  if (right <= x || bottom <= y) return null;
  return { x, y, w: right - x, h: bottom - y };
}

const GAP = 14;
const EDGE = 16;

/** Below, above, right or left of the part, whichever has room; null centres the card. */
export function placeCard(hole: Rect | null, card: { w: number; h: number }, v: { w: number; h: number }): { x: number; y: number } | null {
  if (!hole) return null;
  const clampX = (x: number) => Math.max(EDGE, Math.min(v.w - card.w - EDGE, x));
  const clampY = (y: number) => Math.max(EDGE, Math.min(v.h - card.h - EDGE, y));
  const midX = hole.x + hole.w / 2 - card.w / 2;
  const midY = hole.y + hole.h / 2 - card.h / 2;
  if (v.h - (hole.y + hole.h) >= card.h + GAP + EDGE) return { x: clampX(midX), y: hole.y + hole.h + GAP };
  if (hole.y >= card.h + GAP + EDGE) return { x: clampX(midX), y: hole.y - GAP - card.h };
  if (v.w - (hole.x + hole.w) >= card.w + GAP + EDGE) return { x: hole.x + hole.w + GAP, y: clampY(midY) };
  if (hole.x >= card.w + GAP + EDGE) return { x: hole.x - GAP - card.w, y: clampY(midY) };
  return { x: v.w - card.w - EDGE, y: v.h - card.h - EDGE };
}
